// @flow

import React, {useState, useCallback, useMemo} from 'react';
import debounce from 'lodash.debounce';
import classNames from 'classnames';

import { useSelector, useDispatch } from 'react-redux';
import { log } from '../../utils/jsUtils';
import { createClasses } from '../../utils/stylesUtils';
import useAuthenticatedBackend from '../../hooks/useAuthenticatedBackend';
import AppBar from '@mui/material/AppBar';
import Typography from '@mui/material/Typography';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Hidden from '@mui/material/Hidden';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import { FormControlLabel } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';

import MenuIcon from '@mui/icons-material/Menu';
import DownloadIcon from '@mui/icons-material/CloudDownload';
import EmailIcon from '@mui/icons-material/Email';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';

import MessageTenantDialog from '../Tenants/MessageTenantDialog';
import { REF_CHANNELS } from '../../models/Request';
import ForwardRef from '../SharedComponents/ForwardRef';
import AppLoadingSpinner from '../SharedComponents/AppLoadingSpinner';
import UserProfileMenu from '../UserProfileMenu';
import FilterSelect from '../FilterSelect';
import FilterSearchBox from '../FilterSearchBox';
// import styles from './Styles/MemberHeader.Style';
import memberHeaderStyles from '../Feed/Styles/MemberHeader.Style';
import AddTaskDialog from './AddTaskDialog';
import SPBackend from '../../services/SPBackend';
import { useI18n } from '../../contexts/I18nContext';
import { useGetFilterOptionsQuery } from '../../features/api/feedApi';

import {
  setExportCSVRequests,
  setPrintRequests,
  setFilterSelections,
  setFilterSearchText,
  setFilterOptions,
  selectFilterSelections,
  selectFilterSearchText
} from '../../reduxStore/modules/feed';

import {
  setIsAppLoading,
  setSnackMessage
} from '../../reduxStore/modules/common';

import { selectUser } from '../../reduxStore/modules/user/selectors';

type Props = {
  isDemoMode: Boolean,
  mobileOpen: bool,
  usehandleDrawerToggle: Function,
  sendBroadcast: Function
};

const HeaderMember = (props: Props) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = createClasses(memberHeaderStyles, theme);
  const backend = useAuthenticatedBackend();

  const [openAddTaskDialog, setOpenAddTaskDialog] = useState(false);
  const [openMessagingDialog, setOpenMessagingDialog] = useState(false);
  const [checkedState, setCheckedState] = useState(false);
  
  // Get data from Redux store and RTK Query
  const filterSearchText = useSelector(selectFilterSearchText);
  const filterSelections = useSelector(selectFilterSelections);
  const i18n = useI18n();
  const user = useSelector(selectUser);
  const accountUsage = useSelector(state => state.user.accountUsage);

  // Use RTK Query for filter options
  const { data: filterOptionsData, error: filterOptionsError } = useGetFilterOptionsQuery(
    { userId: user?.uid },
    { skip: !user?.uid }
  );

  // Ensure filterOptions always has the expected structure
  const safeFilterOptions = {
    propertyOptions: filterOptionsData?.propertyOptions || [],
    staffOptions: filterOptionsData?.staffOptions || [],
    tenantOptions: filterOptionsData?.tenantOptions || [],
    tagOptions: filterOptionsData?.tagOptions || [],
    unreadMessagesOnlyOptions: filterOptionsData?.unreadMessagesOnlyOptions || []
  };

  const handleExportCSVClick = useCallback(() => {
    dispatch(setExportCSVRequests(true));
  }, [dispatch]);

  const handlePrintClick = useCallback(() => {
    dispatch(setSnackMessage({
      message: 'Generating your PDF. This will take a moment...',
      level: 'success',
      duration: 5000
    }));
    dispatch(setPrintRequests(true));
  }, [dispatch]);

  // Create a stable debounced function with useCallback and useMemo
  const handleSetSearchInputValue = useCallback(
    debounce(value => {
      dispatch(setFilterSearchText(value));
      dispatch(setIsAppLoading(true));
    }, 300), 
    [dispatch]
  );
  
  // Use useCallback for handleFilterChange to prevent recreating on each render
  const handleFilterChange = useCallback((
      filterName: 'propertyOptions' | 'staffOptions' | 'tenantOptions' | 'tagOptions' | 'unreadMessagesOnlyOptions', 
      selection: Array<?{value: String, label: String}>) => {
    log('[MemberHeader.handleFilterChange] selection',selection);
    selection = selection.filter(select => select.value !== '' && select.label !== '');
    log('[MemberHeader.handleFilterChange] selection after filter',selection);
    
    const newFilterSelections = {
      ...filterSelections,
      [filterName]: selection
    };
    
    log('[MemberHeader.handleFilterChange] newFilterSelections', newFilterSelections);
    dispatch(setFilterSelections(newFilterSelections));
    dispatch(setIsAppLoading(true));
  }, [dispatch, filterSelections]);

  // Use useCallback for handleAddTask to prevent recreating on each render
  const handleAddTask = useCallback((newRequest, selectedUnit) => {
    log('[createTask] newRequest', newRequest);
    if (newRequest.RoomKey === ''){
      dispatch(setSnackMessage({
        message: `${i18n.t('Property')} not found. Please select the ${i18n.t('property')} from the list.`,
        level: 'error',
        duration: 8000
      }));
      return;
    }
    if (newRequest.Issue === ''){
      dispatch(setSnackMessage({
        message: 'Issue not found.',
        level: 'error',
        duration: 3000
      }));
      return;
    }

    // add summary
    let summary = `${i18n.t('Task')} created: ; Source: ${REF_CHANNELS.CONSOLE} ; Additional Info: `;
    if (newRequest.Summary !== '') {
      summary += newRequest.Summary + ';';
    } else {
      summary += 'N/A;';
    }
    // summary += `Building: ${selectedUnit.Property};`;

    const payload = {
      ...newRequest,
      Issue: newRequest.Issue,
      Summary: summary
    };
    log('[handleAddTask] payload ', payload);

    backend.createRequest(payload)
      .then((response) => {
        log('[handleAddTask.createRequest]', response);
        dispatch(setSnackMessage({
          message: `${i18n.t('Task')} created`,
          level: 'success',
          duration: 5000
        }));
      })
      .catch(error => {
        log('[handleAddTask.createRequest] error: ', error);
        dispatch(setSnackMessage({
          message: error.response ? error.response.data : 'An error occured. Please try again',
          level: 'error',
          duration: 5000
        }));
      });
  }, [dispatch, i18n, backend]);

  const {
    isDemoMode,
    usehandleDrawerToggle,
    sendBroadcast
  } = props;
  
  return (
    <AppBar
      sx={classes.appBar}
      position="fixed"
    >
      <Toolbar
        sx={classes.toolbarRoot}
        disableGutters
      >
        <Grid 
          container
          direction='column'
        >
          <Grid
            sx={{
              ...classes.rowSub,
              ...classes.topBar
            }}
            item
          >
            <Grid
              sx={classes.rowSubContainer}
              container
              justifyContent='flex-start'
            >
              <IconButton
                aria-label="Open drawer"
                sx={classes.menuButton}
                onClick={usehandleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>
              <Grid item>
                <Typography
                  sx={classes.pageTitle}
                  variant='h2'
                >{i18n.t('Activity')}
                </Typography>
              </Grid>
              <Hidden smDown>
                <Box
                  sx={classes.filterContainer}
                >
                  <FilterSelect
                    onChangeCallback={response => handleFilterChange('tagOptions', response)}
                    options={safeFilterOptions.tagOptions}
                    placeholder={i18n.t('Tags')}
                    position='first'
                  />
                </Box>
                <Box sx={{ marginLeft: 2 }}>
                  <FilterSearchBox
                    handleSetSearchInputValue={handleSetSearchInputValue}
                    searchInputValue={filterSearchText}
                  />
                </Box>
              </Hidden>
            </Grid>
            <Box 
              sx={{ 
                flexGrow: 1,
                display: 'flex'
              }}
            />
            <UserProfileMenu />
          </Grid>
          <Grid
            sx={{
              ...classes.rowSub,
              ...classes.bottomBar
            }}
            item
          >
            <Grid
              sx={classes.bottomBarContainer}
              container
            >
              {/* <div 
                className={classNames(
                  classes.toggleContainer
                )}
              >
                <Tooltip 
                  aria-label={`Add a ${i18n.t('task')}`}
                  title={`Add a ${i18n.t('task')}`} 
                >
                  <ForwardRef>
                    <Button
                      className={classes.navButton}
                      onClick={() => setOpenAddTaskDialog(true)}
                    >
                      <i className={'icon-add'} />
                      <div>{i18n.t('Task')}</div>
                    </Button>
                  </ForwardRef>
                </Tooltip>
              </div> */}
              
              <Box
                sx={{
                  ...classes.toggleContainer,
                  ...classes.dividerRight
                }}
              >
                <Tooltip 
                  aria-label={'Send a Broadcast'}
                  title={'Send a Broadcast'} 
                >
                  <ForwardRef>
                  <Button
                    sx={classes.navButton}
                    onClick={() => setOpenMessagingDialog(true)}
                    value='Broadcast'
                  >
                    <i className={'icon-sent'} />
                    <div>{'Broadcast'}</div>
                  </Button>
                  {
                    accountUsage
                    ? <MessageTenantDialog
                      open={openMessagingDialog}
                      setOpen={setOpenMessagingDialog}
                      showToFilters
                      triggerAction={sendBroadcast}
                      />
                    : null
                  }
                    {/* <Broadcast
                      scope='all'
                      triggerAction={sendBroadcast}
                    />*/}
                  </ForwardRef> 
                </Tooltip>
              </Box>
              {/* {
                isDemoMode
                ? <div 
                    className={classNames(
                      classes.toggleContainer
                    )}
                  >
                  <Tooltip 
                    aria-label={'Assign'}
                    title={'Assign'} 
                  >
                    <ForwardRef>
                      <Button
                        className={classes.navButton}
                      >
                        <i className={'icon-assign'} />
                        <div>{'Assign'}</div>
                      </Button>
                    </ForwardRef>
                  </Tooltip>
                </div>
                : null
              }
              
              {
                isDemoMode
                ? <div 
                    className={classNames(
                      classes.toggleContainer
                    )}
                  >
                    <Tooltip 
                      aria-label={'Share'}
                      title={'Share'} 
                    >
                      <ForwardRef>
                        <Button
                          className={classes.navButton}
                        >
                          <i className={'icon-share'} />
                          <div>{'Share'}</div>
                        </Button>
                      </ForwardRef>
                    </Tooltip>
                  </div>
                : null
              } */}
              <Box
                sx={classes.grow}
              />
              {/* {
                isDemoMode
                ? <div 
                    className={classNames(
                      classes.toggleContainer
                    )}
                  >
                    <Tooltip 
                      aria-label={'Mark as "in Progress"'}
                      title={'Mark as "in Progress"'} 
                    >
                      <ForwardRef>
                        <Button
                          className={classes.navButton}
                        >
                          <i className={'icon-progress'} />
                          <div>{'In progress'}</div>
                        </Button>
                      </ForwardRef>
                    </Tooltip>
                  </div>
                : null
              }
              {
                isDemoMode
                ? <div 
                    className={classNames(
                      classes.toggleContainer,
                      classes.dividerRight
                    )}
                  >
                    <Tooltip 
                      aria-label={'Mark as "Done"'}
                      title={'Mark as "Done"'} 
                    >
                      <ForwardRef>
                        <Button
                          className={classes.navButton}
                        >
                          <i className={'icon-finished'} />
                          <div>{'Done'}</div>
                        </Button>
                      </ForwardRef>
                    </Tooltip>
                  </div>
                : null
              }

              {
                isDemoMode
                ? <div 
                    className={classNames(
                      classes.toggleContainer
                    )}
                  >
                  <Tooltip 
                    aria-label={'Merge'}
                    title={'Merge'} 
                  >
                    <ForwardRef>
                      <Button
                        className={classes.navButton}
                      >
                        <i className={'icon-merge'} />
                        <div>{'Merge'}</div>
                      </Button>
                    </ForwardRef>
                  </Tooltip>
                </div>
                : null
              }
              {
                isDemoMode
                ? <div 
                    className={classNames(
                      classes.toggleContainer
                    )}
                  >
                  <Tooltip 
                    aria-label={'Split'}
                    title={'Split'} 
                  >
                    <ForwardRef>
                      <Button
                        className={classes.navButton}
                      >
                        <i className={'icon-split'} />
                        <div>{'Split'}</div>
                      </Button>
                    </ForwardRef>
                  </Tooltip>
                </div>
                : null
              }

              <div className={classes.grow} />
              
              {
                isDemoMode
                ? <div 
                    className={classNames(
                      classes.toggleContainer
                    )}
                  >
                  <Tooltip 
                    aria-label={'Sort'}
                    title={'Sort'} 
                  >
                    <ForwardRef>
                      <Button
                        className={classes.navButton}
                      >
                        <i className='icon-sort' />
                        <div>{'Sort'}</div>
                      </Button>
                    </ForwardRef>
                  </Tooltip>
                </div>
                : null
              }
              {/* {
                isDemoMode
                ? <div 
                  className={classNames(
                    classes.toggleContainer
                  )}
                  >
                  <Tooltip 
                    aria-label={'Time'}
                    title={'Time'} 
                  >
                    <ForwardRef>
                      <Button
                        className={classes.navButton}
                      >
                        <i className='icon-calendar' />
                        <div>{'Time'}</div>
                      </Button>
                    </ForwardRef>
                  </Tooltip>
                </div>
                : null
              } */}
              {/* <Box
                sx={classes.toggleContainer}
              >
                <Tooltip
                  aria-label={'Export'}
                  title={'Export'}
                >
                  <ForwardRef>
                    <Button
                      sx={classes.navButton}
                      onClick={() => handleExportCSVClick()}
                    >
                      <DownloadIcon />
                      <div>{'CSV'}</div>
                    </Button>
                  </ForwardRef>
                </Tooltip>
              </Box>
              <Box
                sx={classes.toggleContainer}
              >
                <Tooltip
                  aria-label={'Print to PDF'}
                  title={'Print to PDF'}
                >
                  <ForwardRef>
                    <Button
                      sx={classes.navButton}
                      onClick={() => handlePrintClick()}
                    >
                      <i className={'icon-print'} />
                      <div>{'Print'}</div>
                    </Button>
                  </ForwardRef>
                </Tooltip>
              </Box> */}
              {/* {
                isDemoMode
                ? <div 
                  className={classNames(
                    classes.toggleContainer
                  )}
                  >
                  <Tooltip 
                    aria-label={'Help'}
                    title={'Help'} 
                  >
                    <ForwardRef>
                      <Button
                        className={classes.navButton}
                      >
                        <i className='icon-help' />
                        <div>{'Help'}</div>
                      </Button>
                  </ForwardRef>
                  </Tooltip>
                </div>
                : null
              } */}
            </Grid>
          </Grid>
          <AppLoadingSpinner />
        </Grid>
      </Toolbar>
      <AddTaskDialog
        open={openAddTaskDialog}
        setOpen={setOpenAddTaskDialog}
        triggerAction={handleAddTask}
      />
    </AppBar>
  );
};

export default HeaderMember;
